import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { Link, useParams } from "react-router-dom";
import PlayerTraitBadges from "../components/PlayerTraitBadges";
import { listLeagues, listOrganizations, listPitchLogs, listRosterMemberships, listTeamPlayers, listTeams, listUserAssignments, listUsers as listAppUsers } from "../graphql/queries";
import { listPlayersWithTraits } from "../graphql/playerTraitQueries";
import { createPlayer, createTeamPlayer, createUserAssignment, deleteTeamPlayer, deleteUserAssignment, updatePlayer, updateTeam } from "../graphql/mutations";
import { formatApiError } from "../lib/formatApiError";
import AppDialog from "../components/AppDialog";
import { buildAdminScope } from "../lib/adminScope";
import { buildPlayerTeamIds, getTeamCoachUsernames, type RosterMembershipLike, type UserAssignmentLike } from "../lib/relationshipScope";
import type { ActiveRoleScope } from "../lib/roleContext";

interface Team {
  id: string;
  name: string;
  organizationId?: string | null;
  leagueId?: string | null;
  seasonId?: string | null;
  ageGroup?: string | null;
  coachUsernames?: string[] | null;
}

interface Organization {
  id: string;
  name: string;
}

interface League {
  id: string;
  name: string;
  organizationId: string;
}

interface Player {
  id: string;
  name: string;
  teamId: string;
  pitcher?: boolean | null;
  catcher?: boolean | null;
  battingHandedness?: string | null;
  throwingHandedness?: string | null;
}

interface TeamPlayerLink {
  id: string;
  teamID: string;
  playerID: string;
}

interface PitchLog {
  id: string;
  playerId: string;
  teamId: string;
  date: string;
  pitches: number;
  outsRecorded?: number | null;
  isSubstitute?: boolean | null;
  updatedAt?: string | null;
}

interface AppUser {
  id: string;
  email?: string | null;
  roles?: string[] | null;
}

interface RosterMembership extends RosterMembershipLike {}

interface UserAssignment extends UserAssignmentLike {}

interface TeamDetailPageProps {
  userRoles: string[];
  activeRoleScope?: ActiveRoleScope | null;
}

const formatInningsFromOuts = (outs?: number | null): string => {
  if (typeof outs !== "number" || outs < 0) {
    return "-";
  }
  return `${Math.floor(outs / 3)}.${outs % 3}`;
};

const TeamDetailPage: React.FC<TeamDetailPageProps> = ({ userRoles }) => {
  const { teamId = "" } = useParams<{ teamId: string }>();
  const [username, setUsername] = useState("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayerLink[]>([]);
  const [rosterMemberships, setRosterMemberships] = useState<RosterMembership[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [pitchLogs, setPitchLogs] = useState<PitchLog[]>([]);
  const [coachOptions, setCoachOptions] = useState<Array<{ username: string; label: string }>>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [addCoachOpen, setAddCoachOpen] = useState(false);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);

  const [editName, setEditName] = useState("");
  const [editAgeGroup, setEditAgeGroup] = useState("");
  const [editOrgId, setEditOrgId] = useState("");
  const [editLeagueId, setEditLeagueId] = useState("");

  const [coachQuery, setCoachQuery] = useState("");
  const [pendingCoachUsername, setPendingCoachUsername] = useState("");

  const [playerQuery, setPlayerQuery] = useState("");
  const [pendingPlayerId, setPendingPlayerId] = useState("");

  const adminScope = useMemo(
    () => buildAdminScope({ userRoles, username, organizations, leagues, teams, players, teamPlayers, rosterMemberships }),
    [leagues, organizations, players, rosterMemberships, teamPlayers, teams, userRoles, username]
  );
  const isAdminManager = adminScope.isGlobalAdmin || adminScope.isScopedCommish;
  const isCoachOnly = userRoles.includes("Coach") && !userRoles.includes("Commish") && !userRoles.includes("SuperAdmin");
  const selectedTeam = useMemo(() => teams.find((team) => team.id === teamId), [teamId, teams]);
  const teamCoachUsernames = useMemo(
    () => (selectedTeam ? getTeamCoachUsernames({ team: selectedTeam, userAssignments }) : []),
    [selectedTeam, userAssignments]
  );
  const isTeamCoach = useMemo(() => teamCoachUsernames.includes(username), [teamCoachUsernames, username]);
  const canManagePlayers = (selectedTeam ? adminScope.canManageTeam(selectedTeam) : false) || (userRoles.includes("Coach") && isTeamCoach);
  const canEditTeamDetails = (selectedTeam ? adminScope.canManageTeam(selectedTeam) : false) || (userRoles.includes("Coach") && isTeamCoach);

  const orgById = useMemo(() => organizations.reduce<Record<string, string>>((acc, org) => ({ ...acc, [org.id]: org.name }), {}), [organizations]);
  const leagueById = useMemo(() => leagues.reduce<Record<string, string>>((acc, league) => ({ ...acc, [league.id]: league.name }), {}), [leagues]);

  const playerTeamIds = useMemo(
    () => buildPlayerTeamIds({ players, teamPlayers, rosterMemberships }),
    [players, rosterMemberships, teamPlayers]
  );

  const playersByTeamId = useMemo(() => {
    const out: Record<string, Player[]> = {};
    const playerById = players.reduce<Record<string, Player>>((acc, player) => {
      acc[player.id] = player;
      return acc;
    }, {});
    for (const [playerId, teamIds] of Object.entries(playerTeamIds)) {
      const player = playerById[playerId];
      if (!player) {
        continue;
      }
      for (const membershipTeamId of teamIds) {
        if (!out[membershipTeamId]) {
          out[membershipTeamId] = [];
        }
        if (!out[membershipTeamId].some((item) => item.id === player.id)) {
          out[membershipTeamId].push(player);
        }
      }
    }
    for (const key of Object.keys(out)) {
      out[key].sort((a, b) => a.name.localeCompare(b.name));
    }
    return out;
  }, [playerTeamIds, players]);

  const teamPlayersList = useMemo(() => playersByTeamId[teamId] ?? [], [playersByTeamId, teamId]);

  const teamPlayerStats = useMemo(() => {
    return teamPlayersList
      .map((player) => {
        const playerLogs = pitchLogs
          .filter((log) => log.playerId === player.id && log.teamId === teamId)
          .sort((a, b) => {
            if (a.date === b.date) {
              return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
            }
            return b.date.localeCompare(a.date);
          });

        const totalPitches = playerLogs.reduce((sum, log) => sum + (log.pitches ?? 0), 0);
        const totalOuts = playerLogs.reduce((sum, log) => sum + (typeof log.outsRecorded === "number" ? log.outsRecorded : 0), 0);
        const appearances = playerLogs.length;
        const lastOuting = playerLogs[0];

        return {
          player,
          appearances,
          totalPitches,
          totalOuts,
          totalInnings: formatInningsFromOuts(totalOuts),
          lastOutingDate: lastOuting?.date ?? "-",
          lastOutingPitches: lastOuting?.pitches ?? null
        };
      })
      .sort((a, b) => a.player.name.localeCompare(b.player.name));
  }, [pitchLogs, teamId, teamPlayersList]);

  const existingPlayerIds = useMemo(() => new Set(teamPlayersList.map((player) => player.id)), [teamPlayersList]);
  const filteredPlayerOptions = useMemo(
    () =>
      players
        .filter((player) => !isCoachOnly || (playerTeamIds[player.id] ?? new Set<string>()).has(selectedTeam?.id ?? ""))
        .filter((player) => !existingPlayerIds.has(player.id))
        .filter((player) => player.name.toLowerCase().includes(playerQuery.toLowerCase()))
        .slice(0, 12),
    [existingPlayerIds, isCoachOnly, playerQuery, playerTeamIds, players, selectedTeam?.id]
  );

  const filteredCoachOptions = useMemo(
    () =>
      coachOptions
        .filter((coach) => !teamCoachUsernames.includes(coach.username))
        .filter((coach) => coach.label.toLowerCase().includes(coachQuery.toLowerCase()) || coach.username.toLowerCase().includes(coachQuery.toLowerCase()))
        .slice(0, 10),
    [coachOptions, coachQuery, teamCoachUsernames]
  );

  const loadData = async () => {
    try {
      setErrorMessage("");
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");

      const [teamsResult, orgResult, leagueResult, playersResult, teamPlayersResult, pitchLogsResult, appUsersResult, rosterMembershipsResult, userAssignmentsResult] = await Promise.all([
        API.graphql(graphqlOperation(listTeams, { limit: 500 })),
        API.graphql(graphqlOperation(listOrganizations, { limit: 500 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 500 })),
        API.graphql(graphqlOperation(listPlayersWithTraits, { limit: 1500 })),
        API.graphql(graphqlOperation(listTeamPlayers, { limit: 5000 })),
        API.graphql(graphqlOperation(listPitchLogs, { limit: 6000 })),
        API.graphql(graphqlOperation(listAppUsers, { limit: 500 })),
        API.graphql(graphqlOperation(listRosterMemberships, { limit: 5000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 }))
      ]);

      const loadedTeams = ((teamsResult as any)?.data?.listTeams?.items ?? []).filter(Boolean);
      setTeams(loadedTeams);
      setOrganizations(((orgResult as any)?.data?.listOrganizations?.items ?? []).filter(Boolean));
      setLeagues(((leagueResult as any)?.data?.listLeagues?.items ?? []).filter(Boolean));
      setPlayers(((playersResult as any)?.data?.listPlayers?.items ?? []).filter(Boolean));
      setTeamPlayers(((teamPlayersResult as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean));
      setRosterMemberships(((rosterMembershipsResult as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean));
      setUserAssignments(((userAssignmentsResult as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean));
      setPitchLogs(((pitchLogsResult as any)?.data?.listPitchLogs?.items ?? []).filter(Boolean));

      const appUsers: AppUser[] = ((appUsersResult as any)?.data?.listUsers?.items ?? []).filter(Boolean);
      const coaches = appUsers
        .filter((user) => (user.roles ?? []).some((role) => role === "Coach" || role === "Commish" || role === "SuperAdmin"))
        .map((user) => ({ username: user.id, label: user.email ?? user.id }));
      setCoachOptions(coaches);
    } catch (error) {
      setErrorMessage(`Failed to load team details: ${formatApiError(error)}`);
    }
  };

  useEffect(() => {
    loadData();
  }, [teamId]);

  useEffect(() => {
    if (selectedTeam) {
      setEditName(selectedTeam.name);
      setEditAgeGroup(selectedTeam.ageGroup ?? "");
      setEditOrgId(selectedTeam.organizationId ?? "");
      setEditLeagueId(selectedTeam.leagueId ?? "");
    }
  }, [selectedTeam]);

  const saveTeamEdits = async () => {
    if (!selectedTeam || !canEditTeamDetails || !editName.trim()) {
      return;
    }
    try {
      setErrorMessage("");
      const input: Record<string, unknown> = {
        id: selectedTeam.id
      };

      if (isAdminManager) {
        input.name = editName.trim();
        input.organizationId = editOrgId || null;
      }
      input.ageGroup = editAgeGroup.trim() || null;
      input.leagueId = editLeagueId || null;

      await API.graphql(
        graphqlOperation(updateTeam, {
          input
        })
      );
      setSuccessMessage("Team updated.");
      setEditOpen(false);
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to update team: ${formatApiError(error)}`);
    }
  };

  const addCoachToTeam = async () => {
    if (!selectedTeam || !isAdminManager || !pendingCoachUsername) {
      return;
    }
    try {
      setErrorMessage("");
      const existing = selectedTeam.coachUsernames ?? [];
      if (existing.includes(pendingCoachUsername)) {
        return;
      }
      const updateOps: Promise<any>[] = [
        API.graphql(
          graphqlOperation(updateTeam, {
            input: {
              id: selectedTeam.id,
              coachUsernames: [...existing, pendingCoachUsername]
            }
          })
        ) as Promise<any>
      ];
      const existingAssignment = userAssignments.find(
        (assignment) => assignment.teamId === selectedTeam.id && assignment.userId === pendingCoachUsername && (assignment.relationship ?? "").toLowerCase().includes("coach")
      );
      if (!existingAssignment) {
        updateOps.push(
          API.graphql(
            graphqlOperation(createUserAssignment, {
              input: {
                userId: pendingCoachUsername,
                teamId: selectedTeam.id,
                organizationId: selectedTeam.organizationId ?? null,
                leagueId: selectedTeam.leagueId ?? null,
                relationship: "Coach"
              }
            })
          ) as Promise<any>
        );
      }
      await Promise.all(updateOps);
      setSuccessMessage("Coach added.");
      setPendingCoachUsername("");
      setCoachQuery("");
      setAddCoachOpen(false);
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to add coach: ${formatApiError(error)}`);
    }
  };

  const removeCoachFromTeam = async (coachUsername: string) => {
    if (!selectedTeam || !isAdminManager) {
      return;
    }
    try {
      setErrorMessage("");
      const updated = (selectedTeam.coachUsernames ?? []).filter((item) => item !== coachUsername);
      await API.graphql(graphqlOperation(updateTeam, { input: { id: selectedTeam.id, coachUsernames: updated } }));
      const teamAssignments = userAssignments.filter(
        (assignment) => assignment.teamId === selectedTeam.id && assignment.userId === coachUsername && (assignment.relationship ?? "").toLowerCase().includes("coach")
      );
      await Promise.all(
        teamAssignments.map((assignment) =>
          API.graphql(graphqlOperation(deleteUserAssignment, { input: { id: assignment.id } }))
        )
      );
      setSuccessMessage("Coach removed.");
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to remove coach: ${formatApiError(error)}`);
    }
  };

  const addPlayerToTeam = async () => {
    if (!selectedTeam || !canManagePlayers) {
      return;
    }
    try {
      setErrorMessage("");
      if (pendingPlayerId) {
        const player = players.find((item) => item.id === pendingPlayerId);
        if (!player) {
          return;
        }
        if (player.teamId !== selectedTeam.id) {
          await API.graphql(graphqlOperation(createTeamPlayer, { input: { teamID: selectedTeam.id, playerID: player.id } }));
        }
      } else if (playerQuery.trim()) {
        await API.graphql(graphqlOperation(createPlayer, { input: { name: playerQuery.trim(), teamId: selectedTeam.id } }));
      }
      setPlayerQuery("");
      setPendingPlayerId("");
      setAddPlayerOpen(false);
      setSuccessMessage("Player added to team.");
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to add player: ${formatApiError(error)}`);
    }
  };

  const removePlayerFromTeam = async (player: Player) => {
    if (!selectedTeam || !canManagePlayers) {
      return;
    }
    try {
      setErrorMessage("");
      const linkForTeam = teamPlayers.find((link) => link.teamID === selectedTeam.id && link.playerID === player.id);
      if (player.teamId !== selectedTeam.id) {
        if (linkForTeam) {
          await API.graphql(graphqlOperation(deleteTeamPlayer, { input: { id: linkForTeam.id } }));
        }
        setSuccessMessage("Player removed from this team.");
        await loadData();
        return;
      }

      const alternateLinks = teamPlayers.filter((link) => link.playerID === player.id && link.teamID !== selectedTeam.id);
      if (alternateLinks.length === 0) {
        setErrorMessage("Cannot remove a player from their only team. Reassign the player to another team first.");
        return;
      }

      const nextPrimaryTeamId = alternateLinks[0].teamID;
      await API.graphql(graphqlOperation(updatePlayer, { input: { id: player.id, teamId: nextPrimaryTeamId } }));
      await API.graphql(graphqlOperation(deleteTeamPlayer, { input: { id: alternateLinks[0].id } }));
      setSuccessMessage("Player removed from this team.");
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to remove player from team: ${formatApiError(error)}`);
    }
  };

  if (!selectedTeam) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-4"><Link to="/admin/teams">Back to Teams</Link></div>
        <p>Team not found.</p>
      </div>
    );
  }
  if ((isCoachOnly && !isTeamCoach) || (adminScope.isScopedCommish && selectedTeam && !adminScope.canManageTeam(selectedTeam))) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-4"><Link to="/admin/teams">Back to Teams</Link></div>
        <p>You do not have access to this team.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-4"><Link to="/admin/teams">Back to Teams</Link></div>
      <h1 className="text-2xl font-bold mb-2">{selectedTeam.name}</h1>
      <p className="text-gray-600 mb-4">Team details, coaches, and roster management.</p>
      {errorMessage && <p className="mb-2" style={{ color: "#c1121f" }}>{errorMessage}</p>}
      {successMessage && <p className="mb-4" style={{ color: "#166534" }}>{successMessage}</p>}

      <div className="bubble-card p-4 mb-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="font-semibold">{selectedTeam.name}</div>
            <div className="text-sm text-gray-600">Organization: {selectedTeam.organizationId ? orgById[selectedTeam.organizationId] ?? "Unknown" : "None"}</div>
            <div className="text-sm text-gray-600">League: {selectedTeam.leagueId ? leagueById[selectedTeam.leagueId] ?? "Unknown" : "None"}</div>
            <div className="text-sm text-gray-600">Age Group: {selectedTeam.ageGroup ?? "Not set"}</div>
          </div>
          <div className="flex gap-2">
            {canManagePlayers && (
              <Link to={`/admin/teams/${selectedTeam.id}/lineups`} className="bg-blue-600 text-white px-3 py-2 rounded">
                Lineup Planner
              </Link>
            )}
            {canEditTeamDetails && (
              <button onClick={() => setEditOpen(true)} className="bg-yellow-500 text-white px-3 py-2 rounded">
                Edit Team
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bubble-card p-4 mb-4">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">Coaches</h2>
          {isAdminManager && (
            <button onClick={() => setAddCoachOpen(true)} className="bg-blue-600 text-white px-3 py-2 rounded">
              Add Coach
            </button>
          )}
        </div>
        <div className="bubble-pill-wrap">
          {teamCoachUsernames.length === 0 && <span className="text-gray-500">No coaches assigned.</span>}
          {teamCoachUsernames.map((coachUsername) => {
            const label = coachOptions.find((item) => item.username === coachUsername)?.label ?? coachUsername;
            return (
              <span key={coachUsername} className="bubble-pill">
                {label}
                {isAdminManager && (
                  <button className="ml-2" onClick={() => removeCoachFromTeam(coachUsername)}>x</button>
                )}
              </span>
            );
          })}
        </div>
      </div>

      <div className="bubble-card p-4">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">Players</h2>
          {canManagePlayers && (
            <button onClick={() => setAddPlayerOpen(true)} className="bg-blue-600 text-white px-3 py-2 rounded">
              Add Player
            </button>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Roster with team-specific pitching totals from recorded pitch logs.
        </p>
        <div className="bubble-table-wrapper">
          <table className="bubble-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Appearances</th>
                <th>Total Pitches</th>
                <th>Total Outs</th>
                <th>Total Innings</th>
                <th>Last Outing</th>
                <th>Last Pitches</th>
                {canManagePlayers && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {teamPlayerStats.length === 0 && (
                <tr>
                  <td colSpan={canManagePlayers ? 8 : 7} className="text-gray-600">No players assigned.</td>
                </tr>
              )}
              {teamPlayerStats.map((row) => (
                <tr key={row.player.id}>
                  <td>
                    <Link to={`/admin/players/${row.player.id}`} className="table-sort">
                      {row.player.name}
                    </Link>
                    <div className="mt-1">
                      <PlayerTraitBadges player={row.player} compact />
                    </div>
                  </td>
                  <td>{row.appearances}</td>
                  <td>{row.totalPitches}</td>
                  <td>{row.totalOuts}</td>
                  <td>{row.totalInnings}</td>
                  <td>{row.lastOutingDate}</td>
                  <td>{typeof row.lastOutingPitches === "number" ? row.lastOutingPitches : "-"}</td>
                  {canManagePlayers && (
                    <td>
                      <button className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700" onClick={() => removePlayerFromTeam(row.player)}>
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AppDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Team"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={saveTeamEdits} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
            placeholder="Team name"
            className="border px-3 py-2 rounded"
            disabled={!isAdminManager}
          />
          <input value={editAgeGroup} onChange={(event) => setEditAgeGroup(event.target.value)} placeholder="Age group (11U)" className="border px-3 py-2 rounded" />
          <select value={editOrgId} onChange={(event) => setEditOrgId(event.target.value)} className="border px-3 py-2 rounded" disabled={!isAdminManager}>
            <option value="">No organization</option>
            {organizations.filter((org) => adminScope.isGlobalAdmin || adminScope.canManageOrganization(org.id)).map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
          <select value={editLeagueId} onChange={(event) => setEditLeagueId(event.target.value)} className="border px-3 py-2 rounded">
            <option value="">No league</option>
            {leagues.filter((league) => adminScope.isGlobalAdmin || adminScope.canManageLeague(league.id, selectedTeam?.organizationId)).map((league) => <option key={league.id} value={league.id}>{league.name}</option>)}
          </select>
        </div>
      </AppDialog>

      <AppDialog
        open={addCoachOpen}
        onClose={() => setAddCoachOpen(false)}
        title="Add Coach"
        footer={
          <>
            <button onClick={() => setAddCoachOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={addCoachToTeam} className="bg-blue-600 text-white px-3 py-2 rounded">Add</button>
          </>
        }
      >
        <input
          value={coachQuery}
          onChange={(event) => setCoachQuery(event.target.value)}
          className="border px-3 py-2 rounded w-full mb-2"
          placeholder="Search coach"
        />
        <div className="bubble-pill-wrap">
          {filteredCoachOptions.map((coach) => (
            <button
              key={coach.username}
              className="bubble-pill"
              onClick={() => {
                setPendingCoachUsername(coach.username);
                setCoachQuery(coach.label);
              }}
            >
              {coach.label}
            </button>
          ))}
        </div>
      </AppDialog>

      <AppDialog
        open={addPlayerOpen}
        onClose={() => setAddPlayerOpen(false)}
        title="Add Player To Team"
        footer={
          <>
            <button onClick={() => setAddPlayerOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={addPlayerToTeam} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <input
          value={playerQuery}
          onChange={(event) => {
            setPlayerQuery(event.target.value);
            setPendingPlayerId("");
          }}
          className="border px-3 py-2 rounded w-full mb-2"
          placeholder="Type player name to search or create"
        />
        <div className="bubble-pill-wrap">
          {filteredPlayerOptions.map((player) => (
            <button
              key={player.id}
              className="bubble-pill"
              onClick={() => {
                setPendingPlayerId(player.id);
                setPlayerQuery(player.name);
              }}
            >
              Link: {player.name}
            </button>
          ))}
        </div>
      </AppDialog>
    </div>
  );
};

export default TeamDetailPage;
